import { nameFields } from "./individualName";
import { ownershipKnownNodes, ownershipRowNodeIds, withOwnershipMirrors } from "./editOwnershipPercentage";

// "Edit diagram": the current ownership structure is opened in the visual builder, and on apply
// the edited structure is MERGED into the existing rows (matched by box), instead of replacing
// them. Rows keep their _rowId, so documents, screening and signatory links stay attached.

const clean = (value) => String(value ?? "").trim();
const isClassParty = (party) => /^beneficiary$/i.test(clean(party?.role)) && /^class\b|\bunnamed\b/i.test(clean(party?.name));
const companyKind = (party) => /company|entity|corporate/i.test(clean(party?.trusteeKind || party?.councilKind || party?.kind));

function roleParty(party, withKind) {
  if (!party || !clean(party.name)) return null;
  const company = withKind && companyKind(party);
  return {
    ...nameFields(party),
    name: clean(party.name),
    ...(withKind ? { kind: company ? "company" : "individual" } : {}),
    ...(company
      ? { countryOfIncorporation: clean(party.domicile || party.countryOfIncorporation) }
      : { nationality: clean(party.nationality), domicile: clean(party.domicile) }),
  };
}

/** Table parties of a trust/foundation as the builder's roles (settlor, trustee, …). */
export function rolesFromParties(type, parties) {
  const list = Array.isArray(parties) ? parties : [];
  const byRole = (re) => list.filter((p) => re.test(clean(p?.role)));
  const named = byRole(/^beneficiary$/i).filter((p) => !isClassParty(p) && clean(p?.name));
  const beneficiaries = byRole(/^beneficiary$/i).some(isClassParty)
    ? { kind: "class" }
    : { kind: "named", list: named.map((p) => roleParty(p, false)) };
  if (clean(type) === "Foundation") {
    return {
      kind: "foundation",
      founder: roleParty(byRole(/^founder$/i)[0], false),
      council: roleParty(byRole(/^council/i)[0], true),
      beneficiaries,
      guardian: roleParty(byRole(/^guardian$/i)[0], true),
    };
  }
  return {
    kind: "trust",
    settlor: roleParty(byRole(/^settlor$/i)[0], false),
    trustee: roleParty(byRole(/^trustee/i)[0], true),
    beneficiaries,
    protector: roleParty(byRole(/^protector$/i)[0], true),
  };
}

/** The builder's roles of a trust/foundation as table parties. */
export function partiesFromRoles(type, roles) {
  const r = roles || {};
  const party = (role, p, withKind) => p && clean(p.name) ? {
    role,
    ...nameFields(p),
    name: clean(p.name),
    ...(withKind ? { trusteeKind: p.kind === "company" ? "company" : "individual" } : {}),
    nationality: p.kind === "company" ? "" : clean(p.nationality),
    domicile: clean(p.kind === "company" ? p.countryOfIncorporation : p.domicile),
  } : null;
  const beneficiaries = r.beneficiaries?.kind === "class"
    ? [{ role: "Beneficiary", name: "Class (unnamed)" }]
    : (r.beneficiaries?.list || []).map((b) => party("Beneficiary", b, false));
  const out = clean(type) === "Foundation"
    ? [party("Founder", r.founder, false), party("Council member", r.council, true), ...beneficiaries, party("Guardian", r.guardian, true)]
    : [party("Settlor", r.settlor, false), party("Trustee", r.trustee, true), ...beneficiaries, party("Protector", r.protector, true)];
  return out.filter(Boolean);
}

/**
 * The builder's starting structure from the current structure (the UBO payload nodes, which hold
 * every box however the structure was entered, with trust parties attached). The builder only
 * handles box IDs "X1", "X2", …: boxes are renumbered, each keeping its original ID in sourceId.
 */
export function builderSeedFromNodes(payloadNodes) {
  const nodes = Array.isArray(payloadNodes) ? payloadNodes : [];
  const root = nodes.find((n) => clean(n?.ID) === "X0");
  if (!root) return null;
  const idMap = new Map([["X0", "X0"]]);
  let next = 0;
  // Parents before children, so every child's parent is already renumbered.
  const ordered = [];
  const childrenOf = (id) => nodes.filter((n) => clean(n?.parentIDs?.[0]) === id);
  (function walk(id) {
    childrenOf(id).forEach((child) => {
      const childId = clean(child?.ID);
      if (!childId || idMap.has(childId)) return;
      idMap.set(childId, `X${++next}`);
      ordered.push(child);
      walk(childId);
    });
  })("X0");
  const counts = {};
  // Full parent chain up to the client, as the builder keeps it ([parent, grandparent, …, X0]).
  const byId = new Map(nodes.map((n) => [clean(n?.ID), n]));
  const chainOf = (n) => {
    const chain = [];
    let parentId = clean(n?.parentIDs?.[0]);
    while (parentId && !chain.includes(parentId)) {
      chain.push(parentId);
      if (parentId === "X0") break;
      parentId = clean(byId.get(parentId)?.parentIDs?.[0]);
    }
    return chain;
  };
  const seedNode = (n, isRoot) => {
    const type = clean(n?.type);
    const roles = type === "Trust" || type === "Foundation"
      ? (Array.isArray(n?.parties) && n.parties.some((p) => clean(p?.name)) ? rolesFromParties(type, n.parties) : n?.roles || null)
      : null;
    counts[type] = (counts[type] || 0) + (isRoot ? 0 : 1);
    return {
      ID: idMap.get(clean(n.ID)),
      sourceId: clean(n.ID),
      level: isRoot ? 0 : chainOf(n).length,
      parentIDs: isRoot ? [] : chainOf(n).map((id) => idMap.get(id) || id),
      type,
      typeNo: isRoot ? 0 : counts[type],
      percent: n?.percent === "" || n?.percent == null ? undefined : Number(n.percent),
      boolADE: type !== "Individual",
      ...nameFields(n),
      name: clean(n?.name),
      roles,
      nationality: clean(n?.nationality),
      countryOfResidence: clean(n?.countryOfResidence),
    };
  };
  return {
    initialAHType: clean(root.type),
    nodes: [seedNode(root, true), ...ordered.map((n) => seedNode(n, false))],
    elementCount: next,
    typeCounts: counts,
  };
}

const sameName = (a, b) => clean(a?.name).toLowerCase() === clean(b?.name).toLowerCase();

function mergeParties(previous, edited) {
  const prev = Array.isArray(previous) ? previous : [];
  const used = new Set();
  const merged = (Array.isArray(edited) ? edited : []).map((party) => {
    const index = prev.findIndex((p, i) => !used.has(i) && clean(p?.role).toLowerCase() === clean(party?.role).toLowerCase() && sameName(p, party));
    if (index < 0) return { ...party };
    used.add(index);
    const kept = { ...prev[index] };
    Object.entries(party).forEach(([key, value]) => { if (clean(value) !== "") kept[key] = value; });
    return kept;
  });
  return { parties: merged, removed: prev.filter((p, i) => !used.has(i) && clean(p?.name)) };
}

// Details the builder may hold for a box; the table keeps everything else (DOB, address, …).
function updatedFields(previous, edited, keys) {
  const out = { ...previous };
  keys.forEach((key) => {
    if (edited?.[key] !== undefined && (clean(edited[key]) !== "" || typeof edited[key] === "boolean")) out[key] = edited[key];
  });
  return out;
}

/**
 * Merge the builder's result into the previous ownership (seed: what was opened in the builder). Boxes are matched to rows by their
 * original ID (sourceId); unmatched new boxes become new rows; rows whose box was loaded into the
 * builder but is gone are REMOVED and reported; rows that were never in the diagram are kept.
 * Returns { ownership, removed: [{ collection, row, kind, name, parentName? }] }.
 */
export function mergeEditedOwnership(previous, built, seed) {
  const prev = previous || {};
  const treeNodes = Array.isArray(built?.assistantTree?.nodes) ? built.assistantTree.nodes : [];
  const sourceOf = new Map(treeNodes.map((n) => [clean(n?.ID), clean(n?.sourceId)]));
  // Boxes that were opened in the builder (a row whose box is no longer there was removed).
  const seeded = new Set((seed?.nodes || []).map((n) => clean(n?.sourceId)).filter((id) => id && id !== "X0"));
  const known = ownershipKnownNodes(prev);

  const prevShareholders = Array.isArray(prev.shareholders) ? prev.shareholders : prev.directShareholders || [];
  const prevUbos = Array.isArray(prev.ubos) ? prev.ubos : prev.controllingPersonsNatural || [];
  const prevArrangements = prev.legalArrangements?.arrangements || [];
  const shIds = ownershipRowNodeIds(prevShareholders, known, "SH_rev");
  const uboIds = ownershipRowNodeIds(prevUbos, known, "UBO_rev");
  const arrIds = ownershipRowNodeIds(prevArrangements.map((a) => ({
    treeNodeId: clean(a?._treeArrangementNodeId || a?.treeNodeId || a?.subjectTreeNodeId),
  })), known, "LA_rev");

  const removed = [];
  const rowIdFor = new Map(); // builder row id → final row id

  // Shareholders
  const usedSh = new Set();
  const shareholders = (built?.shareholders || []).map((row) => {
    const index = shIds.findIndex((id, i) => !usedSh.has(i) && id && id === sourceOf.get(clean(row?.treeNodeId)));
    if (index < 0) return { ...row };
    usedSh.add(index);
    const merged = {
      ...updatedFields(prevShareholders[index], row, ["name", "givenNames", "familyName", "holderType", "shareholderEntityType",
        "isPIC", "isHoldingPassive", "isHoldingActive", "isPicOrPassiveHolding", "nationality", "countryOfResidence"]),
      treeNodeId: row.treeNodeId,
      ownershipPercent: clean(row?.ownershipPercent),
    };
    rowIdFor.set(clean(row?._rowId), clean(merged._rowId));
    return merged;
  });
  prevShareholders.forEach((row, i) => {
    if (usedSh.has(i)) return;
    if (seeded.has(shIds[i])) removed.push({ collection: "shareholders", row });
    else shareholders.push(row);
  });
  const finalShareholderRowId = (id) => rowIdFor.get(clean(id)) || clean(id);

  // Controlling persons (trust/foundation role rows of the builder are not table rows)
  const usedUbo = new Set();
  const builtUbos = (built?.ubos || []).filter((u) => !/^(trust|foundation) —/i.test(clean(u?.basisOfControl)));
  const ubos = builtUbos.map((row) => {
    const index = uboIds.findIndex((id, i) => !usedUbo.has(i) && id && id === sourceOf.get(clean(row?.treeNodeId)));
    const base = {
      ...row,
      relatedShareholderRowId: finalShareholderRowId(row?.relatedShareholderRowId),
      ownershipPercent: clean(row?.effectiveOwnershipPercent),
    };
    if (index < 0) return base;
    usedUbo.add(index);
    const previousRow = prevUbos[index];
    return {
      ...updatedFields(previousRow, row, ["name", "givenNames", "familyName", "nationality", "countryOfResidence"]),
      treeNodeId: row.treeNodeId,
      relatedShareholderRowId: base.relatedShareholderRowId,
      relatedShareholderNodeId: clean(row?.relatedShareholderNodeId),
      effectiveOwnershipPercent: clean(row?.effectiveOwnershipPercent),
      ownershipPercent: clean(row?.effectiveOwnershipPercent),
      basisOfControl: clean(previousRow?.basisOfControl) || clean(row?.basisOfControl),
    };
  });
  prevUbos.forEach((row, i) => {
    if (usedUbo.has(i)) return;
    if (seeded.has(uboIds[i])) removed.push({ collection: "ubos", row });
    else ubos.push(row);
  });

  // Trusts and foundations (parties merged by role + name, keeping their table details)
  const usedArr = new Set();
  const arrangements = (built?.arrangements || []).map((arr) => {
    const index = arrIds.findIndex((id, i) => !usedArr.has(i) && id && id === sourceOf.get(clean(arr?._treeArrangementNodeId)));
    const base = { ...arr, sourceRefRowId: finalShareholderRowId(arr?.sourceRefRowId) };
    if (index < 0) return base;
    usedArr.add(index);
    const previousArr = prevArrangements[index];
    const { parties, removed: goneParties } = mergeParties(previousArr?.parties, arr?.parties);
    goneParties.forEach((party) => removed.push({ collection: "parties", row: party, arrangement: previousArr }));
    return {
      ...updatedFields(previousArr, arr, ["name", "givenNames", "familyName", "arrangementType"]),
      _treeArrangementNodeId: arr._treeArrangementNodeId,
      sourceRefRowId: base.sourceRefRowId,
      relatedShareholderName: clean(arr?.relatedShareholderName) || previousArr?.relatedShareholderName,
      parties,
    };
  });
  prevArrangements.forEach((arr, i) => {
    if (usedArr.has(i)) return;
    if (seeded.has(arrIds[i])) { removed.push({ collection: "arrangements", row: arr }); return; }
    // The client itself as a trust/foundation: its parties are the builder's root roles.
    const root = treeNodes.find((n) => clean(n?.ID) === "X0");
    if (clean(arr?.source) === "account_holder" && root?.roles) {
      const { parties, removed: goneParties } = mergeParties(arr.parties, partiesFromRoles(root.type, root.roles));
      goneParties.forEach((party) => removed.push({ collection: "parties", row: party, arrangement: arr }));
      arrangements.push({ ...arr, parties });
      return;
    }
    arrangements.push(arr);
  });

  // The saved diagram: the builder's boxes, without the editing-only sourceId.
  const assistantTree = built?.assistantTree
    ? { ...built.assistantTree, nodes: treeNodes.map(({ sourceId: _source, ...node }) => node) }
    : prev.assistantTree;

  const ownership = withOwnershipMirrors({
    ...prev,
    shareholders,
    ubos,
    legalArrangements: { ...(prev.legalArrangements || {}), arrangements },
    assistantTree,
    assistantGenerated: true,
  });
  return { ownership, removed: removed.map((item) => ({ ...item, ...describeParty(item) })) };
}

function describeParty(item) {
  const row = item.row || {};
  if (item.collection === "arrangements") {
    return { kind: clean(row.arrangementType) === "Foundation" ? "foundation" : "trust", name: clean(row.name) };
  }
  if (item.collection === "parties") {
    return { kind: companyKind(row) ? "company" : "individual", name: clean(row.name), parentName: clean(item.arrangement?.name) };
  }
  const type = clean(row.holderType || row.shareholderEntityType).toLowerCase();
  const kind = type === "trust" || type === "foundation" ? type : type && type !== "individual" ? "company" : "individual";
  return { kind, name: clean(row.name) };
}
